import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Check,
  AlertTriangle,
  CheckCircle,
  Rocket,
  RefreshCw,
  Info,
  Loader,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../reducers';
import { planPricesPerMonth } from '../../../constants/prices';
import planFeatures from '../../plan/upgrade/upgradeView/featureLimits';
import { createOrUpdateSubscription } from '../../plan/thunk';
import { pushModal } from '../../globalModals/globalModalsSlice';
import { setAlertMessage } from '../../dashboard/dashboardSlice';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { cn } from '../../../lib/utils';

interface ChangePlanModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  currentPlan?: string;
  currentTerm?: 'monthly' | 'annual';
  currentPrice?: number;
}

interface PlanInfo {
  key: keyof typeof planPricesPerMonth;
  title: string;
  subtitle: string;
}

const PLAN_INFO: PlanInfo[] = [
  {
    key: 'GROWTH',
    title: 'changePlan.plans.growth.title',
    subtitle: 'changePlan.plans.growth.subtitle',
  },
  {
    key: 'BUSINESS',
    title: 'changePlan.plans.business.title',
    subtitle: 'changePlan.plans.business.subtitle',
  },
  {
    key: 'ENTERPRISE',
    title: 'changePlan.plans.enterprise.title',
    subtitle: 'changePlan.plans.enterprise.subtitle',
  },
];

const ChangePlanModal: React.FC<ChangePlanModalProps> = ({
  open,
  onClose,
  onSuccess,
  currentPlan,
  currentTerm = 'monthly',
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { team } = useSelector((state: RootState) => state);
  const teamId = team.selectedTeam?.teamId || team.teams[0]?.teamId;

  const [selectedTerm, setSelectedTerm] = useState<'monthly' | 'annual'>(
    currentTerm,
  );
  const [selectedPlan, setSelectedPlan] = useState<
    keyof typeof planPricesPerMonth | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const calculateSavings = (plan: keyof typeof planPricesPerMonth) => {
    const monthlyPrice = planPricesPerMonth[plan].monthly;
    const annualPrice = planPricesPerMonth[plan].annual;
    const savings = Math.round(
      ((monthlyPrice - annualPrice) / monthlyPrice) * 100,
    );
    return savings;
  };

  const isCurrentPlan = (plan: keyof typeof planPricesPerMonth) => {
    return currentPlan === plan && currentTerm === selectedTerm;
  };

  const formatPrice = (price: number, term: 'monthly' | 'annual') => {
    return {
      main: `$${price}`,
      period:
        term === 'monthly'
          ? t('changePlan.pricing.perMonth')
          : t('changePlan.pricing.perMonth'),
      billing:
        term === 'annual'
          ? t('changePlan.pricing.billedAnnually', { price: price * 12 })
          : t('changePlan.pricing.billedMonthly'),
    };
  };

  const handlePlanSelect = (plan: keyof typeof planPricesPerMonth) => {
    if (!isCurrentPlan(plan)) {
      setSelectedPlan(plan);
    }
  };

  const handleContinue = () => {
    setShowConfirmation(true);
  };

  const handleBack = () => {
    setShowConfirmation(false);
  };

  const handleConfirmChange = async () => {
    if (!selectedPlan || !teamId) return;

    setIsLoading(true);
    try {
      const newPrice = planPricesPerMonth[selectedPlan][selectedTerm];
      const totalAmount = selectedTerm === 'annual' ? newPrice * 12 : newPrice;

      const result = await dispatch(
        createOrUpdateSubscription({
          teamId,
          plan: selectedPlan,
          term: selectedTerm,
          totalAmount,
        }),
      );

      if (createOrUpdateSubscription.rejected.match(result as never)) {
        const { message = '' } = (result as any).error || {};
        throw new Error(message);
      }

      onClose();
      onSuccess?.();
      dispatch(
        pushModal({
          type: 'upgradeSuccess',
        }),
      );
    } catch (error) {
      console.error('Plan change failed:', error);
      dispatch(
        setAlertMessage({
          type: 'error',
          message: t('changePlan.errors.planChangeFailed', {
            message: error.message,
          }),
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getPlanFeatures = (planKey: keyof typeof planPricesPerMonth) => {
    const features = planFeatures[planKey] || {};
    return Object.entries(features)
      .sort(([, a], [, b]) => a.ranking - b.ranking)
      .map(([, feature]) => ({
        title:
          selectedTerm === 'annual' && feature.annualtitle
            ? feature.annualtitle
            : feature.title,
        description:
          selectedTerm === 'annual' && feature.annualDescription
            ? feature.annualDescription
            : feature.description,
        ranking: feature.ranking,
      }));
  };

  const formatPlanName = (planName: string) => {
    if (!planName) return '';
    const planKey = planName.toLowerCase();
    return t(`changePlan.planNames.${planKey}`, {
      defaultValue:
        planName.charAt(0).toUpperCase() + planName.slice(1).toLowerCase(),
    });
  };

  const getBillingExplanation = () => {
    if (!selectedPlan) return null;

    const isTermChange = currentTerm !== selectedTerm;
    const isPlanChange = currentPlan !== selectedPlan;
    const isAnnualToMonthly =
      currentTerm === 'annual' && selectedTerm === 'monthly';
    const isMonthlyToAnnual =
      currentTerm === 'monthly' && selectedTerm === 'annual';
    const isPlanUpgrade =
      currentPlan &&
      ['GROWTH', 'BUSINESS', 'ENTERPRISE'].indexOf(selectedPlan) >
        ['GROWTH', 'BUSINESS', 'ENTERPRISE'].indexOf(currentPlan);
    const isPlanDowngrade =
      currentPlan &&
      ['GROWTH', 'BUSINESS', 'ENTERPRISE'].indexOf(selectedPlan) <
        ['GROWTH', 'BUSINESS', 'ENTERPRISE'].indexOf(currentPlan);

    let explanationText = '';
    let IconComponent = Info;

    if (isPlanUpgrade && isTermChange && selectedTerm === 'monthly') {
      IconComponent = Rocket;
      explanationText = t('changePlan.billingExplanation.planUpgradeMonthly');
    } else if (isAnnualToMonthly) {
      IconComponent = AlertTriangle;
      explanationText = t('changePlan.billingExplanation.annualToMonthly');
    } else if (isMonthlyToAnnual) {
      IconComponent = CheckCircle;
      explanationText = t('changePlan.billingExplanation.monthlyToAnnual');
    } else if (isPlanUpgrade && !isTermChange) {
      IconComponent = Rocket;
      explanationText = t('changePlan.billingExplanation.planUpgrade');
    } else if (isPlanDowngrade && !isTermChange) {
      IconComponent = AlertTriangle;
      explanationText = t('changePlan.billingExplanation.planDowngrade');
    } else if (isPlanChange && isTermChange) {
      IconComponent = RefreshCw;
      explanationText = t('changePlan.billingExplanation.planAndTermChange');
    } else {
      IconComponent = Info;
      explanationText = t('changePlan.billingExplanation.default');
    }

    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-muted/30 rounded-md mb-4">
        <IconComponent className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
        <p className="text-xs text-muted-foreground leading-none">
          {explanationText}
        </p>
      </div>
    );
  };

  const getTitle = () => {
    return showConfirmation
      ? t('changePlan.titles.reviewChanges')
      : t('changePlan.titles.changePlan');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[95vw] md:max-w-[85vw] lg:max-w-[75vw] xl:max-w-[70vw] w-full sm:w-[640px] md:w-[768px] lg:w-[900px] xl:w-[1024px] max-h-[85vh] overflow-hidden flex flex-col gap-0">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          {!showConfirmation && (
            <DialogDescription>
              {t(
                'changePlan.descriptions.selectPlan',
                'Choose a plan that best fits your needs',
              )}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {!showConfirmation ? (
            <>
              {/* Billing Cycle Tabs */}
              <div className="flex justify-center mb-6">
                <Tabs
                  value={selectedTerm}
                  onValueChange={(value: string) =>
                    setSelectedTerm(value as 'monthly' | 'annual')
                  }
                >
                  <TabsList>
                    <TabsTrigger value="monthly">
                      {t('changePlan.tabs.monthly')}
                    </TabsTrigger>
                    <TabsTrigger value="annual">
                      <span className="flex items-center gap-2">
                        {t('changePlan.tabs.annual')}
                        <Badge
                          variant="secondary"
                          className="bg-green-100 text-green-800 hover:bg-green-100 hover:text-green-800"
                        >
                          {t('changePlan.tabs.annualDiscount')}
                        </Badge>
                      </span>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Plan Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-1">
                {PLAN_INFO.map((plan) => {
                  const pricing = formatPrice(
                    planPricesPerMonth[plan.key][selectedTerm],
                    selectedTerm,
                  );
                  const isCurrent = isCurrentPlan(plan.key);
                  const isSelected = selectedPlan === plan.key;
                  const savings =
                    selectedTerm === 'annual' ? calculateSavings(plan.key) : 0;

                  return (
                    <Card
                      key={plan.key}
                      onClick={() => handlePlanSelect(plan.key)}
                      className={cn(
                        'relative transition-all',
                        !isCurrent && 'cursor-pointer',
                        isCurrent && 'cursor-default',
                        isSelected && !isCurrent && 'border-2 border-primary',
                        !isSelected &&
                          !isCurrent &&
                          'border-2 border-transparent',
                      )}
                    >
                      {isCurrent && (
                        <Badge
                          className="absolute top-4 right-4"
                          variant="default"
                        >
                          {t('changePlan.labels.currentPlan')}
                        </Badge>
                      )}

                      <CardHeader className="pb-4">
                        <h3 className="text-lg font-semibold">
                          {t(plan.title)}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {t(plan.subtitle)}
                        </p>
                      </CardHeader>

                      <CardContent className="space-y-4">
                        {/* Pricing */}
                        <div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold text-primary">
                              {pricing.main}
                            </span>
                            <span className="text-muted-foreground">
                              {pricing.period}
                            </span>
                            {selectedTerm === 'annual' && savings > 0 && (
                              <Badge
                                variant="secondary"
                                className="ml-2 bg-green-100 text-green-800 hover:bg-green-100 hover:text-green-800"
                              >
                                {t('changePlan.labels.percentOff', {
                                  percent: savings,
                                })}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {pricing.billing}
                          </p>
                        </div>

                        {/* Features */}
                        <ScrollArea className="h-[200px] pr-2">
                          <ul className="space-y-2">
                            {getPlanFeatures(plan.key).map((feature, index) => (
                              <li
                                key={index}
                                className="flex items-start gap-2"
                              >
                                <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <span className="text-sm text-muted-foreground">
                                  {t(feature.title)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </ScrollArea>

                        {/* Action Button */}
                        <Button
                          variant={
                            isSelected && !isCurrent ? 'default' : 'outline'
                          }
                          className="w-full"
                          disabled={isCurrent}
                        >
                          {(() => {
                            if (isCurrent)
                              return t('changePlan.labels.currentPlan');
                            if (isSelected)
                              return t('changePlan.labels.selected');
                            return t('changePlan.labels.selectPlan');
                          })()}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          ) : (
            showConfirmation &&
            selectedPlan && (
              /* Confirmation Screen */
              <div className="space-y-4">
                {/* Billing Information */}
                {getBillingExplanation()}

                {/* Plan Change Summary */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium mb-3">
                      {t('changePlan.summary.title')}
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-muted-foreground">
                          {t('changePlan.summary.currentPlan')}
                        </span>
                        <span className="text-sm">
                          {formatPlanName(currentPlan || '')} (
                          {t(`changePlan.terms.${currentTerm}`)})
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-muted-foreground">
                          {t('changePlan.summary.newPlan')}
                        </span>
                        <span className="text-sm font-medium">
                          {formatPlanName(selectedPlan)} (
                          {t(`changePlan.terms.${selectedTerm}`)})
                        </span>
                      </div>
                      <div className="border-t pt-3">
                        <div className="flex justify-between items-start">
                          <span className="text-sm text-muted-foreground">
                            {t('changePlan.summary.newPrice')}
                          </span>
                          <div className="text-right">
                            <div className="flex items-baseline gap-1">
                              <span className="text-lg font-semibold">
                                $
                                {planPricesPerMonth[selectedPlan][selectedTerm]}
                              </span>
                              <span className="text-sm text-muted-foreground">
                                {t('changePlan.pricing.perMonth')}
                              </span>
                            </div>
                            {selectedTerm === 'annual' && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {t('changePlan.summary.billedAnnually', {
                                  price:
                                    planPricesPerMonth[selectedPlan][
                                      selectedTerm
                                    ] * 12,
                                })}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          )}
        </div>

        <DialogFooter className="gap-2">
          {!showConfirmation ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={isLoading}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleContinue}
                disabled={!selectedPlan || isLoading}
              >
                {t('changePlan.buttons.continue')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={isLoading}
              >
                {t('changePlan.buttons.back')}
              </Button>
              <Button onClick={handleConfirmChange} disabled={isLoading}>
                {isLoading && <Loader className="mr-2 h-4 w-4 animate-spin" />}
                {t('changePlan.buttons.confirmChange')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePlanModal;
